import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sun, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

export const MorningDigestCard = () => {
  const [s, setS] = useState(null);
  const [preview, setPreview] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/digest/settings`, cfg).then(r => setS(r.data)).catch(() => toast.error('Failed to load digest settings'));
  }, []);

  const update = async (patch) => {
    try {
      const r = await axios.put(`${BACKEND_URL}/api/digest/settings`, patch, cfg);
      setS(r.data);
      toast.success('Digest settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };

  const showPreview = async () => {
    try { setPreview((await axios.get(`${BACKEND_URL}/api/digest/preview`, cfg)).data.preview); }
    catch { toast.error('Failed to build preview'); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/digest/send-now`, {}, cfg);
      setPreview(r.data.preview);
      r.data.ok ? toast.success('Digest sent to your WhatsApp') : toast.error(r.data.error || 'Send failed');
      setS((await axios.get(`${BACKEND_URL}/api/digest/settings`, cfg)).data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); } finally { setSending(false); }
  };

  if (!s) return null;
  const last = s.last_status;

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4" data-testid="morning-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><Sun className="w-4 h-4 text-amber-600" /> Morning WhatsApp Digest</h2>
          <p className="text-xs text-stone-500 mt-1">Sent to the admin number at 8:00 AM IST, Monday to Saturday — next 3 tasks per project, carried-over items, stalled projects and quick jobs.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-600" data-testid="digest-enabled-label">{s.enabled ? 'On' : 'Paused'}</span>
          <Switch checked={s.enabled} onCheckedChange={(v) => update({ enabled: v })} data-testid="digest-enabled-switch" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-stone-600">
        Flag project as stalled after
        <Select value={String(s.stall_days)} onValueChange={(v) => update({ stall_days: Number(v) })}>
          <SelectTrigger className="h-8 w-24 text-xs border-stone-200" data-testid="digest-stall-days"><SelectValue /></SelectTrigger>
          <SelectContent>{[2, 3, 5, 7, 14].map(d => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={showPreview} data-testid="digest-preview-btn"><Eye className="w-3.5 h-3.5 mr-1.5" />Preview</Button>
        <Button size="sm" onClick={sendNow} disabled={sending} data-testid="digest-send-now-btn" className="bg-green-700 hover:bg-green-800 text-white">
          <Send className="w-3.5 h-3.5 mr-1.5" />{sending ? 'Sending…' : 'Send digest now'}
        </Button>
      </div>
      {last && (
        <p className={`text-xs ${last.ok ? 'text-green-700' : 'text-red-700'}`} data-testid="digest-last-status">
          Last {last.trigger === 'manual' ? 'test' : 'scheduled'} send: {new Date(last.at).toLocaleString('en-IN')} — {last.ok ? `delivered to Twilio${last.parts > 1 ? ` (${last.parts} messages)` : ''}` : `failed: ${last.error}`}
        </p>
      )}
      {preview && (
        <pre className="text-xs bg-stone-50 border border-stone-200 rounded-lg p-3 whitespace-pre-wrap font-sans text-stone-800 max-h-80 overflow-y-auto" data-testid="digest-preview">{preview}</pre>
      )}
    </div>
  );
};
